import "@/styles/application.css";
import { createRoot } from "react-dom/client";
import { createInertiaApp } from "@inertiajs/react";
import React from "react";
import { Toaster } from "sonner";
import { initSentry, setSentryUser } from "@/lib/sentry";
import SentryErrorBoundary from "@components/SentryErrorBoundary";
import type { LocaleDict } from "@/lib/i18n";

// Sentry はアプリ描画より前に初期化する（初期レンダリング中の例外も捕捉するため）。
// VITE_SENTRY_DSN が空なら no-op。
initSentry();

// Inertia ページを2系統からまとめて解決:
//   1) app/frontend/pages/**            (パッケージ横断の共通ページ)
//   2) app/packages/<pkg>/app/frontend/pages/**  (パッケージ別ページ)
const sharedPages = import.meta.glob("../pages/**/*.tsx");
const packagePages = import.meta.glob("../../packages/*/app/frontend/pages/**/*.tsx");

function resolveByName(name: string) {
  // 共通領域から探す
  const sharedKey = `../pages/${name}.tsx`;
  if (sharedPages[sharedKey]) return sharedPages[sharedKey]();

  // パッケージ命名規約: "<package>/<Page>" 例: "home/Index" → home パッケージの pages/Index.tsx
  const [pkg, ...rest] = name.split("/");
  const pkgKey = `../../packages/${pkg}/app/frontend/pages/${rest.join("/")}.tsx`;
  if (packagePages[pkgKey]) return packagePages[pkgKey]();

  throw new Error(`Inertia page not found: ${name}`);
}

createInertiaApp({
  resolve: (name: string) => resolveByName(name) as Promise<{ default: React.ComponentType }>,
  setup({ el, App, props }) {
    // Inertia の共有 props からユーザーコンテキストと i18n 辞書を取り出す。
    const sharedProps = props.initialPage.props as {
      i18n?: LocaleDict;
      currentUserId?: number | string | null;
      currentUserEmail?: string | null;
    };
    setSentryUser({ id: sharedProps.currentUserId, email: sharedProps.currentUserEmail });

    createRoot(el).render(
      <SentryErrorBoundary i18n={sharedProps.i18n ?? {}}>
        <App {...props} />
        <Toaster position="top-right" richColors closeButton />
      </SentryErrorBoundary>,
    );
  },
});
